import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { formatPrice } from "../lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-carbon/80 z-[80]"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[460px] bg-graphite z-[90] flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-smoke">
              <h2 className="font-heading text-2xl text-ivory">Carrito</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="text-ivory hover:text-gold">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <p className="overline mb-4">Vacío</p>
                  <p className="font-heading text-2xl text-ivory mb-6">Su carrito está vacío</p>
                  <Link to="/shop" onClick={() => setOpen(false)} className="btn-outline-gold inline-block">
                    Descubrir colecciones
                  </Link>
                </div>
              ) : items.map(item => (
                <div key={item.product_id} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-20 h-24 flex-shrink-0 overflow-hidden bg-smoke">
                    {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-base text-ivory truncate">{item.name}</p>
                    <p className="text-xs text-ivory/50 uppercase tracking-wider mt-1">{item.sku}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-smoke">
                        <button data-testid={`qty-minus-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity - 1)} className="px-2 py-1 text-ivory hover:text-gold"><Minus size={12} /></button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button data-testid={`qty-plus-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity + 1)} className="px-2 py-1 text-ivory hover:text-gold"><Plus size={12} /></button>
                      </div>
                      <p className="text-gold font-heading">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                  <button data-testid={`remove-${item.product_id}`} onClick={() => removeItem(item.product_id)} className="text-ivory/40 hover:text-gold self-start"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-smoke p-6 space-y-4">
                <div className="flex justify-between items-baseline">
                  <span className="overline">Subtotal</span>
                  <span className="font-heading text-2xl text-gold">{formatPrice(subtotal)}</span>
                </div>
                <p className="text-xs text-ivory/50">IVA y envío calculados en el checkout</p>
                <Link to="/checkout" onClick={() => setOpen(false)} data-testid="cart-checkout-btn" className="btn-gold block text-center w-full">
                  Finalizar compra
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
